import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

const DEFAULT_SYMBOLS = ['AAPL','MSFT','NVDA','AVGO','GOOG','TSLA','JPM','V'];

function LoadingSpinner({ size = 16, color = 'white' }) {
  return (
    <div style={{
      width: size,
      height: size,
      border: `2px solid ${color}40`,
      borderTop: `2px solid ${color}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      display: 'inline-block',
      marginRight: 8
    }} />
  );
}

function formatPrice(price, market) {
  if (!price || price === 0) return '—';

  const currencySymbols = {
    'US': '$',
    'CA': 'C$',
    'VN': '₫'
  };

  const symbol = currencySymbols[market] || '$';

  if (market === 'VN') {
    // For VND, show full price with proper symbol
    return symbol + price.toFixed(0);
  }

  // For other markets, show normal price with 2 decimals
  return symbol + price.toFixed(2);
}

export default function StockList({ symbols = DEFAULT_SYMBOLS, market: externalMarket, onMarketChange, pollInterval = 15000 }){
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [market, setMarket] = useState(externalMarket || 'US');
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [sortBy, setSortBy] = useState('symbol');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(false);
  const aiRef = useRef(null);
  const API_BASE = import.meta.env.VITE_API_URL || 'https://market360-api.onrender.com';

  const PAGE_SIZE = 10;

  // Sync external market prop
  useEffect(() => {
    if (externalMarket && externalMarket !== market) {
      setMarket(externalMarket);
      setPage(0);
    }
  }, [externalMarket]);

  const fetchMarketStocks = useCallback(async (reset = false, customOffset = null) => {
    if (loading) return;
    setLoading(true);
    try {
      const offset = customOffset !== null ? customOffset : (reset ? 0 : page * PAGE_SIZE);
      const res = await axios.get(`${API_BASE}/api/market/${market}?limit=${PAGE_SIZE}&offset=${offset}&sort_by=${sortBy}&sort_order=${sortOrder}&fetch_prices=false`);
      const stocks = Array.isArray(res.data) ? res.data : (res.data?.rows || []);

      // Fetch real-time prices in batch
      let priceData = [];
      try {
        const symbols = [...new Set(stocks.map(s => s.symbol))].join(',');
        if (symbols) {
          const response = await axios.get(`${API_BASE}/api/yahoo/prices?symbols=${symbols}`);
          priceData = Array.isArray(response.data)
            ? response.data
            : (response.data && typeof response.data === 'object' ? Object.values(response.data) : []);
        }
      } catch (err) {
        priceData = [];
      }

      const priceMap = {};
      priceData.forEach(p => {
        if (p && p.symbol) priceMap[p.symbol] = p;
      });

      const enrichedStocks = stocks.map((stock) => {
        const realtime = priceMap[stock.symbol];
        const price = realtime?.price ?? stock.price ?? stock.last_price ?? 0;
        const change = realtime?.change_percent ?? stock.change_percent ?? stock.change ?? 0;
        const spark = stock.spark || [];
        const name = stock.name || realtime?.name || '';
        return {
          ...stock,
          price,
          change,
          spark,
          name,
          market: stock.market || market
        };
      });

      // Enrich with latest saved suggestions (regime) when available
      try {
        const sugRes = await axios.get(`${API_BASE}/api/suggestions/market/${market}?limit=${stocks.length}`);
        const sugList = Array.isArray(sugRes.data) ? sugRes.data : (sugRes.data?.rows || []);
        const sugMap = {};
        (sugList || []).forEach(s => { if (s && s.symbol) sugMap[s.symbol] = s; });
        for (let i = 0; i < enrichedStocks.length; i++) {
          const sym = enrichedStocks[i].symbol;
          enrichedStocks[i].regime = sugMap[sym]?.regime || null;
        }
      } catch (e) {
        // non-fatal
        console.debug('No suggestions available for market', market, e);
      }

      if (reset) {
        setItems(enrichedStocks);
      } else {
        setItems(prev => [...prev, ...enrichedStocks]);
      }
    } catch (e) {
      console.error('Error fetching market stocks:', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [market, sortBy, sortOrder, loading, API_BASE, page]);

  useEffect(() => {
    mounted.current = true;
    setPage(0);
    setHasMore(true);
    fetchMarketStocks(true);
    return () => { mounted.current = false; };
  }, [market, sortBy, sortOrder]);

  useEffect(() => {
    if (page > 0) {
      const offset = page * PAGE_SIZE;
      fetchMarketStocks(false, offset);
    }
  }, [page]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    const symbol = searchTerm.trim().toUpperCase();

    // Basic symbol validation
    if (symbol.length < 1 || symbol.length > 10) {
      alert('Stock symbol must be between 1 and 10 characters.');
      return;
    }

    // Check for invalid characters (allow letters, numbers, dots, hyphens)
    if (!/^[A-Z0-9.-]+$/.test(symbol)) {
      alert('Stock symbol can only contain letters, numbers, dots, and hyphens.');
      return;
    }

    try {
      // Validate symbol via Yahoo details before adding
      let detailsOk = false;
      try {
        const det = await axios.get(`${API_BASE}/api/yahoo/details/${symbol}`);
        if (det.data && !det.data.error && (det.data.name || det.data.price)) {
          detailsOk = true;
        }
      } catch (e) {
        // treat as not ok
        detailsOk = false;
      }

      if (!detailsOk) {
        const proceed = window.confirm(`${symbol} could not be verified on Yahoo Finance. Add anyway?`);
        if (!proceed) return;
      }

      // First try to add the stock to the market
      const response = await axios.post(`${API_BASE}/api/market/${market}/add`, null, { params: { symbol } });
      const data = response.data;

      // Show result message (if warning, show it separately)
      if (data.warning) {
        alert(`${data.message || 'Added'} — note: ${data.warning}`);
      } else {
        alert(data.message || 'Stock added successfully!');
      }

      // Then refresh the list
      setPage(0);
      setHasMore(true);
      await fetchMarketStocks(true);
      setSearchTerm('');
    } catch (e) {
      console.error('Error adding stock:', e);
      const errorMessage = e.response?.data?.error || 'Error adding stock. Please check the symbol.';
      alert(errorMessage);
    }
  };

    // Debounced suggestion/filtering for search box
    useEffect(() => {
      const term = (searchTerm || '').trim().toUpperCase();
      if (!term) {
        setSuggestions([]);
        setShowSuggest(false);
        return;
      }
      const id = setTimeout(() => {
        const local = items.filter(it => (it.symbol || '').toUpperCase().includes(term) || (it.name || '').toUpperCase().includes(term));
        if (local.length) {
          setSuggestions(local.slice(0, 8));
          setFocusedIndex(0);
          setShowSuggest(true);
        } else {
          setSuggestions([{ symbol: term, name: `Search Yahoo for ${term}`, _is_external: true }]);
          setFocusedIndex(0);
          setShowSuggest(true);
        }
      }, 250);
      return () => clearTimeout(id);
    }, [searchTerm, items]);

    const handleSuggestionClick = async (s) => {
      if (!s) return;
      if (!s._is_external && s.symbol) {
        window.location.href = `/stock/${s.symbol}`;
        return;
      }
      const symbol = s.symbol;
      try {
        // validate first
        let detailsOk = false;
        try {
          const det = await axios.get(`${API_BASE}/api/yahoo/details/${symbol}`);
          if (det.data && !det.data.error && (det.data.name || det.data.price)) detailsOk = true;
        } catch (e) { detailsOk = false; }
        if (!detailsOk) {
          const proceed = window.confirm(`${symbol} could not be verified on Yahoo Finance. Add anyway?`);
          if (!proceed) return;
        }
        const res = await axios.post(`${API_BASE}/api/market/${market}/add`, null, { params: { symbol } });
        await fetchMarketStocks(true);
        setSearchTerm('');
        setShowSuggest(false);
        window.location.href = `/stock/${symbol}`;
      } catch (e) {
        console.error('Error adding from suggestion', e);
        alert('Could not find symbol on Yahoo or add it to the database.');
      }
    };

  // Polling for real-time prices
  useEffect(() => {
    if (items.length === 0 || pollInterval <= 0) return;
    
    const pollPrices = async () => {
      try {
        const symbols = [...new Set(items.map(item => item.symbol))].join(',');
        const response = await axios.get(`${API_BASE}/api/yahoo/prices?symbols=${symbols}`);
        let priceData = response.data;
        if (!Array.isArray(priceData)) {
          console.warn('Polling prices: non-array response, coercing', priceData);
          if (priceData && typeof priceData === 'object') {
            priceData = Object.values(priceData).filter(x => x && (x.symbol || x.price || x.error));
          } else {
            priceData = [];
          }
        }

        setItems(prevItems => 
          prevItems.map(item => {
            const update = Array.isArray(priceData) ? priceData.find(p => p.symbol === item.symbol) : undefined;
            if (update && !update.error) {
              return {
                ...item,
                price: update.price,
                change: update.change_percent,
                // Update sparkline with new price
                spark: [...item.spark.slice(1), update.price]
              };
            }
            return item;
          })
        );
      } catch (e) {
        console.error('Error polling prices:', e);
      }
    };

    const intervalId = setInterval(pollPrices, pollInterval);
    return () => clearInterval(intervalId);
  }, [items.length, pollInterval, API_BASE]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(0);
    setHasMore(true);
  };

  const handleKeyDown = (e) => {
    if (!showSuggest || !suggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearch();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(i => Math.min((i === -1 ? 0 : i + 1), suggestions.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(i => Math.max((i === -1 ? 0 : i - 1), 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const s = suggestions[focusedIndex >= 0 ? focusedIndex : 0];
      if (s) handleSuggestionClick(s);
      return;
    }
    if (e.key === 'Escape') {
      setShowSuggest(false);
      return;
    }
  };

  const loadMore = () => {
    if (hasMore && !loading) {
      setPage(prev => prev + 1);
    }
  };

  return (
    <section className="stock-list">
      {/* Market Tabs */}
      <div className="market-tabs" style={{display:'flex', justifyContent:'center', gap:12, marginBottom:24, marginTop:16}}>
        {['US', 'CA', 'VN'].map(m => (
          <button 
            key={m} 
            className={`market-tab ${market === m ? 'active' : ''}`}
            onClick={() => setMarket(m)}
            style={{
              padding: '12px 24px',
              border: market === m ? '2px solid var(--accent-blue)' : '2px solid var(--border-color)',
              background: market === m ? 'var(--gradient-blue)' : 'var(--bg-secondary)',
              color: market === m ? 'white' : 'var(--text-primary)',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '14px',
              transition: 'all 0.3s ease',
              boxShadow: market === m ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none',
              minWidth: '80px'
            }}
            onMouseEnter={(e) => {
              if (market !== m) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 8px rgba(255, 255, 255, 0.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (market !== m) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }
            }}
          >
            {m}
          </button>
        ))}
      </div>

      {/* Search Box */}
      <div className="search-box" style={{display:'flex', gap:8, marginBottom:16}}>
        <input
          type="text"
          placeholder="Search or add stock symbol..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => handleKeyDown && handleKeyDown(e)}
          style={{
            flex:1,
            padding: '12px 16px',
            border: '2px solid var(--border-color)',
            borderRadius: 8,
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            fontSize: '14px',
            transition: 'border-color 0.3s ease',
            outline: 'none'
          }}
          onFocus={(e) => e.target.style.borderColor = 'var(--accent-blue)'}
          onBlur={(e) => { e.target.style.borderColor = 'var(--border-color)'; setTimeout(() => setShowSuggest(false), 150); }}
        />
        <button 
          onClick={handleSearch}
          style={{
            padding: '12px 20px',
            background: 'var(--gradient-blue)',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.2)';
          }}
        >
          Add
        </button>
      </div>

      {/* Suggestion Dropdown */}
      {showSuggest && suggestions && suggestions.length > 0 && (
        <div style={{position:'relative', maxWidth:800, margin:'0 auto 12px'}}>
          <div style={{position:'absolute', left:0, right:0, background:'var(--bg-secondary)', border:'1px solid var(--border-color)', borderRadius:8, padding:8, zIndex:50}}>
            {suggestions.map((s, idx) => (
              <div
                key={s.symbol + (s._is_external?'-ext':'')}
                onMouseDown={() => handleSuggestionClick(s)}
                onMouseEnter={() => setFocusedIndex(idx)}
                style={{
                  padding:'8px 12px',
                  cursor:'pointer',
                  borderRadius:6,
                  display:'flex',
                  justifyContent:'space-between',
                  background: idx === focusedIndex ? 'var(--primary)' : 'transparent',
                  color: idx === focusedIndex ? 'white' : 'var(--text-primary)'
                }}
              >
                <div>
                  <strong style={{marginRight:8}}>{s.symbol}</strong>
                  <span style={{color: idx === focusedIndex ? 'white' : 'var(--text-secondary)'}}>{s.name || ''}</span>
                </div>
                {s._is_external ? <span style={{fontSize:12, color: idx === focusedIndex ? 'white' : 'var(--text-secondary)'}}>Search</span> : <span style={{fontSize:12, color: idx === focusedIndex ? 'white' : 'var(--text-secondary)'}}>Open</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sort Buttons */}
      <div className="sort-buttons" style={{display:'flex', gap:8, marginBottom:16}}>
        {[
          {key: 'symbol', label: 'Symbol'},
          {key: 'price', label: 'Price'},
          {key: 'change', label: 'Change'}
        ].map(s => (
          <button 
            key={s.key} 
            onClick={() => handleSort(s.key)}
            style={{
              padding: '4px 8px',
              border: '1px solid var(--border)',
              background: sortBy === s.key ? 'var(--primary)' : 'var(--bg-secondary)',
              color: sortBy === s.key ? 'white' : 'var(--text)',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            {s.label} {sortBy === s.key ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
          </button>
        ))}
      </div>

      <div className="stock-items">
        {items.map(it => (
          <Link to={`/stock/${it.symbol}`} className="stock-row" key={it.symbol}>
            <div className="stock-left">
              <div className="symbol">{it.symbol}</div>
              <div className="company">{it.name}</div>
            </div>
            <div className="stock-center">
              <div className="mini-chart">
                <Line
                  data={{ labels: it.spark.map((_,i)=>i), datasets: [{ data: it.spark, borderColor: (it.change && it.change>=0)? '#10b981':'#ef4444', borderWidth: 2, pointRadius:0 }] }}
                  options={{ responsive:true, maintainAspectRatio:false, plugins:{legend:{display:false}, tooltip:{enabled:false}}, scales:{x:{display:false}, y:{display:false}} }}
                />
              </div>
            </div>
            <div className="stock-right">
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <div className="price">{formatPrice(it.price, it.market)}</div>
                {it.regime ? (
                  <span style={{padding:'4px 8px', borderRadius:8, fontSize:12, fontWeight:700, background: it.regime === 'bull' ? '#d1fae5' : it.regime === 'bear' ? '#fee2e2' : '#f0f9ff', color: it.regime === 'bull' ? '#065f46' : it.regime === 'bear' ? '#991b1b' : '#0369a1'}}>{String(it.regime).toUpperCase()}</span>
                ) : null}
              </div>
              <div className={`badge ${it.change && it.change>=0? 'up':'down'}`} style={{marginTop:6}}>{it.change ? (it.change>=0? `+${it.change.toFixed(2)}`:it.change.toFixed(2)) : '--'}</div>
            </div>
          </Link>
        ))}
      </div>

      {/* AI Panel removed when empty to avoid blank box on mobile */}

      {/* Load More Button */}
      {hasMore && (
        <div style={{textAlign:'center', marginTop:20}}>
          <button 
            onClick={loadMore}
            disabled={loading}
            style={{
              padding: '12px 20px',
              background: 'var(--gradient-blue)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.8 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              minWidth: 120,
              width: 'auto',
              maxWidth: 420,
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)'
            }}
          >
            {loading && <LoadingSpinner size={16} />}
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
      
      {/* Scroll to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          background: 'var(--accent-blue)',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          fontWeight: 'bold',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          zIndex: 1000
        }}
        title="Scroll to top"
      >
        ↑
      </button>
    </section>
  );
}

